/* eslint-disable react/no-unescaped-entities */
import { useState, useRef } from "react";
import AppShell, { SectionCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { UploadSimple, CheckCircle, Warning, FileArrowUp, ArrowClockwise, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Bulk Employee Import (CSV)
 *  Step 1 — Upload CSV → /api/employees/bulk-import/dry-run (validates only)
 *  Step 2 — Preview report; if no errors → apply
 *  Step 3 — POST /api/employees/bulk-import/apply with the same CSV + flags
 *
 * Supports the comprehensive employee schema documented in SCHEMA below.
 */

const SCHEMA = [
  // Identity & employment
  { col: "email",                     req: false, ex: "jane.doe@acme.io",          desc: "Work email (unique per company). Used as login if accounts are created. Leave blank for blue-collar / field / contractor employees without an email." },
  { col: "name",                      req: true,  ex: "Jane Doe",                  desc: "Full legal name." },
  { col: "employee_code",             req: false, ex: "EMP-1001",                  desc: "Your internal employee ID. Auto-generated if blank." },
  { col: "job_title",                 req: false, ex: "Senior Engineer",           desc: "Current job title." },
  { col: "designation",               req: false, ex: "M-3",                       desc: "Internal grade / band (alias of job_title)." },
  { col: "department",                req: false, ex: "Engineering",               desc: "Department name (must match existing department or it's stored as text)." },
  { col: "branch_code",               req: false, ex: "BLR-HQ",                    desc: "Branch code (Locations → Branch detail → code). Unknown codes raise a warning." },
  { col: "employee_type",             req: false, ex: "wfo",                       desc: "wfo | wfh | field | hybrid · default wfo" },
  { col: "employment_class",          req: false, ex: "on_roll",                   desc: "on_roll | off_roll_consultant | off_roll_contractor | intern · default on_roll" },
  { col: "role_in_company",           req: false, ex: "employee",                  desc: "employee | branch_manager | sub_manager | assistant_manager | company_admin" },
  // Contact / personal
  { col: "phone",                     req: false, ex: "+91-9999900000",            desc: "Mobile / work phone." },
  { col: "personal_email",            req: false, ex: "jane@gmail.com",            desc: "Personal email (for relieving letters, etc.)." },
  { col: "gender",                    req: false, ex: "female",                    desc: "male | female | other | prefer_not_to_say" },
  { col: "date_of_birth",             req: false, ex: "1992-03-14",                desc: "YYYY-MM-DD" },
  { col: "marital_status",            req: false, ex: "single",                    desc: "single | married | other" },
  // Joining / probation
  { col: "date_of_joining",           req: false, ex: "2026-04-01",                desc: "YYYY-MM-DD" },
  { col: "probation_months",          req: false, ex: "6",                         desc: "Whole number — months of probation." },
  { col: "confirmation_date",         req: false, ex: "2026-10-01",                desc: "YYYY-MM-DD — when employee was confirmed." },
  // Compensation
  { col: "ctc_annual",                req: false, ex: "2400000",                   desc: "Annual CTC (positive number, no commas)." },
  { col: "currency",                  req: false, ex: "INR",                       desc: "ISO 4217 code (INR / USD / SGD / GBP …). Default INR." },
  // Reporting
  { col: "manager_email",             req: false, ex: "manager@acme.io",           desc: "Email of an existing employee. Unknown emails raise a warning." },
  // Address
  { col: "address_line1",             req: false, ex: "42 MG Road",                desc: "Current residential address line 1." },
  { col: "address_line2",             req: false, ex: "Suite 200",                 desc: "Line 2 / building / landmark." },
  { col: "city",                      req: false, ex: "Bengaluru",                 desc: "" },
  { col: "state",                     req: false, ex: "Karnataka",                 desc: "" },
  { col: "country",                   req: false, ex: "India",                     desc: "" },
  { col: "pincode",                   req: false, ex: "560001",                    desc: "ZIP / PIN code." },
  // Statutory (India)
  { col: "pan",                       req: false, ex: "AAAPA9999A",                desc: "Indian PAN (10 chars). Warns on format mismatch." },
  { col: "aadhaar_last4",             req: false, ex: "1234",                      desc: "Last 4 digits ONLY. Full Aadhaar is never stored." },
  { col: "uan_number",                req: false, ex: "100123456789",              desc: "12-digit Universal Account Number (PF)." },
  { col: "pf_number",                 req: false, ex: "KN/BLR/12345/0001",         desc: "Employer-issued PF account." },
  { col: "esic_number",               req: false, ex: "3112345678",                desc: "10-digit ESIC number." },
  // Bank
  { col: "bank_name",                 req: false, ex: "HDFC Bank",                 desc: "Salary bank." },
  { col: "bank_account_number",       req: false, ex: "50100123456789",            desc: "Salary account number." },
  { col: "bank_ifsc",                 req: false, ex: "HDFC0000123",               desc: "IFSC code." },
  // Emergency
  { col: "emergency_contact_name",    req: false, ex: "Ravi Doe",                  desc: "Person to contact in emergency." },
  { col: "emergency_contact_relation",req: false, ex: "Spouse",                    desc: "Relationship." },
  { col: "emergency_contact_phone",   req: false, ex: "+91-9888800000",            desc: "" },
];

export default function BulkEmployeeImport() {
  const [step, setStep] = useState("upload");
  const [report, setReport] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [createUsers, setCreateUsers] = useState(false);
  const [skipExisting, setSkipExisting] = useState(true);
  const [autoStartOnboarding, setAutoStartOnboarding] = useState(false);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [showSchema, setShowSchema] = useState(false);
  // Missing-masters resolution state — keyed by entity → value → { action, ...spec }
  const [resolutions, setResolutions] = useState({ branches: {}, managers: {}, designations: {}, departments: {} });
  const [existingMasters, setExistingMasters] = useState({ branches: [], designations: [], departments: [], managers: [] });
  const inputRef = useRef(null);

  const downloadTemplate = (variant = "full") => {
    const headers = SCHEMA.map(s => s.col);
    // 2 placeholder rows showing both required-only and fully-populated styles
    const exampleFull = SCHEMA.map(s => s.ex);
    const exampleMinimal = SCHEMA.map(s => (s.req ? s.ex.replace("Jane", "John").replace("jane", "john").replace("Doe", "Roe").replace("doe", "roe") : ""));
    const rows = variant === "minimal" ? [exampleMinimal] : [exampleFull, exampleMinimal];
    const csv = [headers.join(","), ...rows.map(r => r.map(c => /[,"\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c).join(","))].join("\n") + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = variant === "minimal" ? "employee-import-minimal.csv" : "employee-import-template.csv";
    a.click();
  };

  const onFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) return toast.error("Please upload a .csv file");
    setBusy(true);
    setCsvFile(file);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/employees/bulk-import/dry-run", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReport(data); setStep("preview");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  const apply = async () => {
    if (!csvFile) return toast.error("Re-upload to apply");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      fd.append("skip_existing", skipExisting ? "true" : "false");
      fd.append("create_user_accounts", createUsers ? "true" : "false");
      fd.append("auto_start_onboarding", autoStartOnboarding ? "true" : "false");
      fd.append("send_welcome_email", sendWelcomeEmail ? "true" : "false");
      // Only include entities the admin actually acted on.
      const trimmed = Object.fromEntries(
        Object.entries(resolutions).filter(([, v]) => v && Object.keys(v).length > 0)
      );
      fd.append("master_resolutions", JSON.stringify(trimmed));
      const { data } = await api.post("/employees/bulk-import/apply", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.created} employees`);
      setReport({ ...report, ...data }); setStep("done");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  };

  const goToReviewMasters = async () => {
    // Pre-load master lists so the Map dropdowns work.
    try {
      const [br, ds, dp, em] = await Promise.all([
        api.get("/org/branches").catch(() => ({ data: [] })),
        api.get("/designations").catch(() => ({ data: [] })),
        api.get("/org/departments").catch(() => ({ data: [] })),
        api.get("/employees").catch(() => ({ data: [] })),
      ]);
      const asList = (x) => (Array.isArray(x) ? x : (x?.items || x?.data || []));
      setExistingMasters({
        branches: asList(br.data),
        designations: asList(ds.data),
        departments: asList(dp.data),
        managers: asList(em.data),
      });
    } catch { /* proceed with empty lists — user can still create/skip */ }
    setStep("review");
  };

  const setResolution = (entity, value, spec) => {
    setResolutions((r) => ({
      ...r,
      [entity]: { ...r[entity], [value]: spec },
    }));
  };
  const clearResolution = (entity, value) => {
    setResolutions((r) => {
      const copy = { ...r[entity] };
      delete copy[value];
      return { ...r, [entity]: copy };
    });
  };

  const reset = () => {
    setStep("upload"); setReport(null); setCsvFile(null);
    setResolutions({ branches: {}, managers: {}, designations: {}, departments: {} });
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <AppShell title="Bulk Employee Import">
      {step === "upload" && (
        <>
          <SectionCard
            title="Step 1 · Upload CSV"
            subtitle="We validate first (dry-run), preview errors, then apply only on your confirmation."
            testid="section-bulk-upload"
            action={
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="h-9 gap-1.5" onClick={() => downloadTemplate("minimal")} data-testid="bi-minimal-btn">
                  <FileArrowUp size={14}/> Minimal template
                </Button>
                <Button size="sm" variant="outline" className="h-9 gap-1.5" onClick={() => downloadTemplate("full")} data-testid="bi-template-btn">
                  <FileArrowUp size={14}/> Full template (all {SCHEMA.length} columns)
                </Button>
              </div>
            }>
            <div
              className="border-2 border-dashed border-zinc-300 rounded-lg p-12 text-center hover:border-zinc-500 transition cursor-pointer"
              onClick={() => inputRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
              data-testid="bi-dropzone"
            >
              <UploadSimple size={36} className="mx-auto text-zinc-400 mb-2"/>
              <div className="text-sm font-medium text-zinc-700">Drop your CSV here</div>
              <div className="text-xs text-zinc-500 mt-1">or click to browse · max 5000 rows · UTF-8</div>
              <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={e => onFile(e.target.files?.[0])}/>
            </div>
            <div className="mt-4 grid md:grid-cols-2 gap-3 text-xs">
              <div className="border border-emerald-200 bg-emerald-50/50 rounded-md p-3">
                <div className="font-semibold text-emerald-800 mb-1">Required</div>
                <code className="text-[11px] text-emerald-900">name</code>
                <div className="text-[10px] text-emerald-800/70 mt-1">Email is optional — leave blank for blue-collar / field / contractor employees without a work email.</div>
              </div>
              <div className="border border-zinc-200 bg-zinc-50 rounded-md p-3">
                <div className="font-semibold text-zinc-800 mb-1">Optional ({SCHEMA.filter(s => !s.req).length} columns)</div>
                <div className="text-[11px] text-zinc-600">Identity, contact, address, statutory (PAN / UAN), bank, emergency contact, and more — see full schema below.</div>
              </div>
            </div>
            {busy && <div className="mt-4 text-sm text-zinc-500 flex items-center gap-2"><ArrowClockwise size={14} className="animate-spin"/> Validating…</div>}
          </SectionCard>

          {/* Schema reference */}
          <div className="mt-6 border border-zinc-200 rounded-lg bg-white">
            <button onClick={() => setShowSchema(s => !s)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-zinc-50" data-testid="bi-schema-toggle">
              <div>
                <div className="font-semibold text-zinc-900">Column reference — every supported field</div>
                <div className="text-xs text-zinc-500 mt-0.5">Each column with its meaning, allowed values, and an example. Click any cell to copy.</div>
              </div>
              <CaretDown size={18} className={`transition-transform ${showSchema ? "rotate-180" : ""}`}/>
            </button>
            {showSchema && (
              <div className="border-t border-zinc-200 px-5 py-4" data-testid="bi-schema-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                        <th className="py-2 pr-3 font-medium">Column</th>
                        <th className="py-2 pr-3 font-medium">Required</th>
                        <th className="py-2 pr-3 font-medium">Example</th>
                        <th className="py-2 font-medium">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {SCHEMA.map((s) => (
                        <tr key={s.col} className="border-b border-zinc-100 hover:bg-zinc-50">
                          <td className="py-2 pr-3 font-mono text-[12px] text-zinc-800">{s.col}</td>
                          <td className="py-2 pr-3 text-[12px]">
                            {s.req ? <span className="text-rose-700 font-medium">Required</span> : <span className="text-zinc-400">Optional</span>}
                          </td>
                          <td className="py-2 pr-3 font-mono text-[11px] text-zinc-600 cursor-pointer" onClick={() => { navigator.clipboard.writeText(s.ex); toast.success(`Copied: ${s.ex}`); }}>{s.ex}</td>
                          <td className="py-2 text-[12px] text-zinc-700 leading-snug">{s.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 text-xs text-zinc-500 leading-relaxed border-t border-zinc-100 pt-3">
                  <strong className="text-zinc-700">Tips:</strong> dates must be <code className="bg-zinc-100 px-1 rounded">YYYY-MM-DD</code>;
                  enum values are <em>case-insensitive</em>; unknown <code className="bg-zinc-100 px-1 rounded">branch_code</code> or
                  <code className="bg-zinc-100 px-1 rounded">manager_email</code> raise warnings (not errors) — the employee is created without that link.
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {step === "preview" && report && (
        <SectionCard
          title={`Step 2 · Preview · ${(report.valid_rows ?? report.row_count) ?? 0} valid · ${report.errors?.length ?? 0} errors · ${report.warnings?.length ?? 0} warnings`}
          subtitle={report.errors?.length ? "Fix errors and re-upload before applying." : "Looks clean — choose options and apply."}
          testid="section-bulk-preview"
          action={
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="h-9" onClick={reset} data-testid="bi-reset-btn">Re-upload</Button>
              <Button size="sm" className="h-9 gap-1.5" onClick={apply} disabled={busy || (report.errors?.length || 0) > 0} data-testid="bi-apply-btn">
                <CheckCircle size={14}/> {busy ? "Applying…" : `Apply ${(report.valid_rows ?? report.new_rows) ?? 0}`}
              </Button>
            </div>
          }>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <label className="flex items-center gap-2 text-sm border border-zinc-200 rounded p-2.5">
              <Switch checked={skipExisting} onCheckedChange={setSkipExisting} data-testid="bi-skip-existing"/>
              <div>
                <div className="font-medium">Skip existing employees</div>
                <div className="text-xs text-zinc-500">If a row's email already exists, skip it (don't update)</div>
              </div>
            </label>
            <label className="flex items-center gap-2 text-sm border border-zinc-200 rounded p-2.5">
              <Switch checked={createUsers} onCheckedChange={setCreateUsers} data-testid="bi-create-users"/>
              <div>
                <div className="font-medium">Create login accounts</div>
                <div className="text-xs text-zinc-500">Each new employee gets a User row with default password</div>
              </div>
            </label>
            <label className="flex items-center gap-2 text-sm border border-zinc-200 rounded p-2.5">
              <Switch checked={autoStartOnboarding} onCheckedChange={setAutoStartOnboarding} data-testid="bi-auto-onboarding"/>
              <div>
                <div className="font-medium">Auto-start onboarding</div>
                <div className="text-xs text-zinc-500">Only for new joiners. Leave this OFF when importing existing staff — otherwise everyone is marked "onboarding".</div>
              </div>
            </label>
            <label className={`flex items-center gap-2 text-sm border rounded p-2.5`}>
              <Switch checked={sendWelcomeEmail} onCheckedChange={setSendWelcomeEmail} data-testid="bi-welcome-email"/>
              <div>
                <div className="font-medium">Email onboarding magic link</div>
                <div className="text-xs text-zinc-500">Each new hire gets a magic-link email to the self-service onboarding wizard (KYC, banking, education).</div>
              </div>
            </label>
          </div>
          {(report.errors?.length || 0) > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs mb-3" data-testid="bi-error-banner">
              <div className="flex items-start gap-2 text-red-800 font-semibold mb-2"><Warning size={14} className="mt-0.5 flex-none"/> {report.errors.length} validation errors</div>
              <ul className="space-y-0.5 max-h-60 overflow-y-auto">
                {report.errors.slice(0, 30).map((err, i) => (
                  <li key={i} className="text-red-700"><span className="font-mono text-[11px]">row {err.row}</span> · {err.field || "—"} → {err.message}</li>
                ))}
                {report.errors.length > 30 && <li className="text-zinc-500 italic">…and {report.errors.length - 30} more</li>}
              </ul>
            </div>
          )}
          {(report.warnings?.length || 0) > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs mb-3" data-testid="bi-warning-banner">
              <div className="font-semibold text-amber-800 mb-1">{report.warnings.length} warnings (not blocking)</div>
              <ul className="space-y-0.5 max-h-32 overflow-y-auto">
                {report.warnings.slice(0, 15).map((w, i) => (
                  <li key={i} className="text-amber-700"><span className="font-mono text-[11px]">row {w.row}</span> · {w.message}</li>
                ))}
              </ul>
            </div>
          )}
          {(() => {
            const mm = report.missing_masters || {};
            const totalMissing = Object.values(mm).reduce((s, arr) => s + (arr?.length || 0), 0);
            const resolvedCount = Object.values(resolutions).reduce((s, obj) => s + Object.keys(obj || {}).length, 0);
            if (totalMissing === 0) return null;
            return (
              <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 text-xs mb-3 flex items-start justify-between gap-3" data-testid="bi-missing-masters-banner">
                <div>
                  <div className="font-semibold text-indigo-800 mb-1">
                    {totalMissing} value{totalMissing > 1 ? "s" : ""} in your CSV don&apos;t exist yet
                    {resolvedCount > 0 ? ` · ${resolvedCount} resolved` : ""}
                  </div>
                  <div className="text-indigo-700">
                    Choose to create them, map to existing masters, or skip those rows — instead of importing employees with missing links.
                  </div>
                </div>
                <Button size="sm" className="h-8 shrink-0" onClick={goToReviewMasters} data-testid="bi-review-masters-btn">
                  Review &amp; fix →
                </Button>
              </div>
            );
          })()}
          {(report.errors?.length || 0) === 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs text-emerald-800 flex items-center gap-2">
              <CheckCircle size={14}/> All {report.valid_rows ?? report.row_count} rows pass validation. Click <strong>Apply</strong> when ready.
            </div>
          )}
        </SectionCard>
      )}

      {step === "review" && report && (
        <ReviewMastersStep
          missing={report.missing_masters || {}}
          existing={existingMasters}
          resolutions={resolutions}
          setResolution={setResolution}
          clearResolution={clearResolution}
          onBack={() => setStep("preview")}
        />
      )}

      {step === "done" && report && (
        <SectionCard title="Step 3 · Done" subtitle={`Imported ${report.created || 0} · skipped ${report.skipped || 0}`} testid="section-bulk-done">
          <div className="border border-emerald-200 bg-emerald-50/50 rounded-lg py-12 text-center" data-testid="bi-done">
            <CheckCircle size={36} className="mx-auto text-emerald-600 mb-2"/>
            <div className="text-lg font-semibold">{report.created || 0} employees imported</div>
            {report.skipped ? <div className="text-xs text-zinc-500 mt-1">{report.skipped} skipped (already existed)</div> : null}
            {report.skipped_by_masters ? <div className="text-xs text-zinc-500 mt-1">{report.skipped_by_masters} skipped (missing master values marked as skip)</div> : null}
            {report.masters_resolved ? (
              <div className="text-xs text-zinc-500 mt-1">
                {["branches", "designations", "departments", "managers"].filter(k => report.masters_resolved[k] > 0).map(k =>
                  `${report.masters_resolved[k]} new ${k}`).join(" · ") || null}
              </div>
            ) : null}
            <div className="mt-4 grid grid-cols-2 gap-2 max-w-md mx-auto text-xs text-zinc-700">
              {!!report.onboardings_started && (
                <div className="border border-emerald-200 bg-white rounded px-3 py-2" data-testid="bi-done-onboardings">
                  <div className="font-semibold text-emerald-700">{report.onboardings_started}</div>
                  <div>onboardings auto-started</div>
                </div>
              )}
              {!!report.welcome_emails_sent && (
                <div className="border border-blue-200 bg-white rounded px-3 py-2" data-testid="bi-done-welcomes">
                  <div className="font-semibold text-blue-700">{report.welcome_emails_sent}</div>
                  <div>welcome emails sent</div>
                </div>
              )}
              {!!report.welcome_emails_failed && (
                <div className="border border-amber-200 bg-white rounded px-3 py-2 col-span-2" data-testid="bi-done-emails-failed">
                  <div className="text-amber-700"><strong>{report.welcome_emails_failed}</strong> welcome email(s) could not be sent. Check Resend domain verification.</div>
                </div>
              )}
              {report.default_template_missing && (
                <div className="border border-amber-200 bg-white rounded px-3 py-2 col-span-2" data-testid="bi-done-no-template">
                  <div className="text-amber-700">Onboarding auto-start skipped — no default template found for this company.</div>
                </div>
              )}
            </div>
            <div className="mt-4 flex gap-2 justify-center">
              <Button size="sm" variant="outline" onClick={reset} data-testid="bi-another-btn">Import another</Button>
              <Button size="sm" variant="outline" onClick={() => window.location.href = "/app/onboarding"} data-testid="bi-onb-btn">Open onboarding queue</Button>
              <Button size="sm" onClick={() => window.location.href = "/app/employees"} data-testid="bi-dir-btn">Go to Directory</Button>
            </div>
          </div>
        </SectionCard>
      )}
    </AppShell>
  );
}


// ---------------------------------------------------------------------------
// Review & Fix step — one card per entity, radio choice per missing value.
// ---------------------------------------------------------------------------
const ENTITY_META = {
  branches:     { label: "Branches",     help: "Location codes referenced in the CSV but not in your Locations master.",     targetLabel: "Map to existing branch",     createLabel: "Create branch" },
  managers:     { label: "Managers",     help: "Manager emails referenced in the CSV but no employee row exists.",           targetLabel: "Map to existing employee",   createLabel: "Invite as stub employee" },
  designations: { label: "Designations", help: "Titles referenced in the CSV but not in your Designations master.",           targetLabel: "Map to existing designation", createLabel: "Create designation" },
  departments:  { label: "Departments",  help: "Department names referenced in the CSV but not in your Departments master.",  targetLabel: "Map to existing department",  createLabel: "Create department" },
};

function ReviewMastersStep({ missing, existing, resolutions, setResolution, clearResolution, onBack }) {
  const entities = Object.keys(missing).filter((e) => missing[e]?.length > 0);
  const totalMissing = entities.reduce((s, e) => s + (missing[e]?.length || 0), 0);
  const resolvedCount = entities.reduce((s, e) => s + Object.keys(resolutions[e] || {}).length, 0);

  return (
    <SectionCard
      title={`Step 2b · Review missing masters (${resolvedCount}/${totalMissing} resolved)`}
      subtitle="For each unresolved value: create a new master, map to an existing one, or skip those rows entirely."
      testid="section-review-masters"
      action={
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-9" onClick={onBack} data-testid="rm-back-btn">← Back to preview</Button>
          <Button size="sm" className="h-9" onClick={onBack} data-testid="rm-done-btn">Done · Continue to apply</Button>
        </div>
      }
    >
      <div className="space-y-5">
        {entities.map((entity) => (
          <EntityBlock
            key={entity}
            entity={entity}
            values={missing[entity]}
            targets={existing[entity] || []}
            resolutions={resolutions[entity] || {}}
            setResolution={(v, spec) => setResolution(entity, v, spec)}
            clearResolution={(v) => clearResolution(entity, v)}
          />
        ))}
        {entities.length === 0 && (
          <div className="text-sm text-zinc-500">Nothing to review — all master values in your CSV are valid.</div>
        )}
      </div>
    </SectionCard>
  );
}

function EntityBlock({ entity, values, targets, resolutions, setResolution, clearResolution }) {
  const meta = ENTITY_META[entity];
  return (
    <div className="border border-zinc-200 rounded-lg overflow-hidden" data-testid={`rm-block-${entity}`}>
      <div className="bg-zinc-50 px-4 py-2.5 border-b border-zinc-200">
        <div className="font-semibold text-sm text-zinc-800">{meta.label} ({values.length} missing)</div>
        <div className="text-[11px] text-zinc-500 mt-0.5">{meta.help}</div>
      </div>
      <div className="divide-y divide-zinc-100">
        {values.map((row) => (
          <ValueRow
            key={row.value}
            entity={entity}
            row={row}
            meta={meta}
            targets={targets}
            current={resolutions[row.value]}
            onSet={(spec) => setResolution(row.value, spec)}
            onClear={() => clearResolution(row.value)}
          />
        ))}
      </div>
    </div>
  );
}

function ValueRow({ entity, row, meta, targets, current, onSet, onClear }) {
  const [action, setAction] = useState(current?.action || "");

  const pickCreate = () => { setAction("create"); onSet({ action: "create" }); };
  const pickMap = () => { setAction("map"); onSet({ action: "map", target_id: current?.target_id || (targets[0]?.id || "") }); };
  const pickSkip = () => { setAction("skip"); onSet({ action: "skip" }); };
  const pickInviteStub = () => { setAction("invite_stub"); onSet({ action: "invite_stub" }); };

  return (
    <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-start sm:gap-4 text-sm" data-testid={`rm-row-${entity}-${row.value.replace(/\W+/g, "-")}`}>
      <div className="sm:w-64 shrink-0 mb-2 sm:mb-0">
        <div className="font-mono text-[13px] text-zinc-900 break-all">{row.value}</div>
        <div className="text-[11px] text-zinc-500 mt-0.5">
          used in {row.row_count} row{row.row_count > 1 ? "s" : ""}
          {row.sample_rows?.length ? ` · rows ${row.sample_rows.join(", ")}` : ""}
        </div>
      </div>
      <div className="flex-1 space-y-1.5">
        <label className={`flex items-center gap-2 px-2.5 py-1.5 rounded border cursor-pointer ${action === (entity === "managers" ? "invite_stub" : "create") ? "border-emerald-300 bg-emerald-50" : "border-zinc-200 hover:bg-zinc-50"}`}>
          <input type="radio" checked={action === (entity === "managers" ? "invite_stub" : "create")} onChange={entity === "managers" ? pickInviteStub : pickCreate} data-testid={`rm-radio-create-${entity}-${row.value.replace(/\W+/g, "-")}`}/>
          <span className="text-[13px]">{meta.createLabel}</span>
        </label>
        <label className={`flex items-center gap-2 px-2.5 py-1.5 rounded border cursor-pointer ${action === "map" ? "border-indigo-300 bg-indigo-50" : "border-zinc-200 hover:bg-zinc-50"}`}>
          <input type="radio" checked={action === "map"} onChange={pickMap} disabled={targets.length === 0}/>
          <span className="text-[13px]">{meta.targetLabel}</span>
          {action === "map" && targets.length > 0 && (
            <select
              className="ml-2 text-[12px] border border-zinc-300 rounded px-1.5 py-0.5"
              value={current?.target_id || ""}
              onChange={(e) => onSet({ action: "map", target_id: e.target.value })}
              data-testid={`rm-map-select-${entity}-${row.value.replace(/\W+/g, "-")}`}
            >
              {targets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name || t.title || t.email || t.code || t.id}
                </option>
              ))}
            </select>
          )}
          {targets.length === 0 && <span className="ml-2 text-[10px] text-zinc-400 italic">no existing records</span>}
        </label>
        <label className={`flex items-center gap-2 px-2.5 py-1.5 rounded border cursor-pointer ${action === "skip" ? "border-red-300 bg-red-50" : "border-zinc-200 hover:bg-zinc-50"}`}>
          <input type="radio" checked={action === "skip"} onChange={pickSkip}/>
          <span className="text-[13px]">Skip these {row.row_count} rows</span>
        </label>
        {action && (
          <button className="text-[11px] text-zinc-500 hover:text-zinc-700 underline" onClick={() => { setAction(""); onClear(); }}>Clear choice</button>
        )}
      </div>
    </div>
  );
}
